import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCancelFormDeliverable } from '../../components/form-list/useCancelFormDeliverable';
import { useDeleteApiRipsByIdMutation } from '../../app/athenaApiGen';
import { FormList } from '../../components/form-list';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import { WORKFLOW_PROGRESS_FILTERS } from '../../utils/workflow';
import { createRIPColumns } from './RIPUtils';
import { useRIPList } from './useRIPList';
import {
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  setWorkflowFilter,
} from '../../app/rip/ripSlice';

/**
 * Provides RIP listing parity with TR/TAR pages using the shared FormList widget.
 */
export default function RIPList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get state from Redux
  const { isDeleteDialogOpen, ripToDelete, snackbar } = useSelector((state) => state.rip);
  const workflowFilter = useSelector((state) => state.rip.workflowFilter);

  const {
    paginationModel,
    setPaginationModel,
    search,
    setSearch,
    rows,
    rowCount,
    loading,
    isError,
    refetch,
    quickFilterCounts,
  } = useRIPList();
  const [deleteRip, { isLoading: deleting }] = useDeleteApiRipsByIdMutation();

  const columns = useMemo(() => createRIPColumns(), []);

  const handleDeleteClick = useCallback((row) => {
    dispatch(openDeleteDialog({ id: row.id, ripNumber: row.ripNumber }));
  }, [dispatch]);

  const handleDeleteConfirm = useCallback(
    async () => {
      if (!ripToDelete) return;
      try {
        await deleteRip({ id: ripToDelete.id }).unwrap();
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'RIP deleted successfully', severity: 'success' }));
        refetch();
      } catch (error) {
        console.error('Failed to delete RIP', error);
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'Failed to delete RIP. Please try again.', severity: 'error' }));
      }
    },
    [ripToDelete, deleteRip, refetch, dispatch],
  );

  const notifyCancel = useCallback(
    (message, severity) => dispatch(showSnackbar({ message, severity })),
    [dispatch],
  );
  const { handleCancelClick } = useCancelFormDeliverable({
    entityLabel: 'RIP',
    notify: notifyCancel,
    refetch,
  });

  const rowActions = useMemo(
    () => [
      {
        label: 'View',
        onClick: (row) => navigate(`/rip/${row.id}?mode=view`),
      },
      {
        label: 'Edit',
        onClick: (row) => navigate(`/rip/${row.id}?mode=edit`),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
      {
        label: 'Cancel',
        onClick: (row) => handleCancelClick(row),
        hidden: (row) =>
          !row.workflowState ||
          row.workflowState === 'Pending' ||
          row.workflowState !== 'In Internal Review',
      },
      {
        label: 'Delete',
        onClick: (row) => handleDeleteClick(row),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
    ],
    [handleDeleteClick, handleCancelClick, navigate],
  );

  return (
    <Box sx={{ p: 1, pt: 2, height: '100%', bgcolor: 'transparent' }}>
      <FormList
        title="Request to Initiate Purchase (RIPs)"
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        error={isError}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowActions={rowActions}
        onRowClick={(row) => navigate(`/rip/${row.id}?mode=view`)}
        workflowFilters={WORKFLOW_PROGRESS_FILTERS}
        workflowFilter={workflowFilter}
        onWorkflowFilterChange={(value) => dispatch(setWorkflowFilter(value))}
        quickFilterCounts={quickFilterCounts}
        search={search}
        onSearchChange={setSearch}
        onRefresh={refetch}
        createButtonLabel="New RIP"
        onCreateClick={() => navigate('/rip')}
      />

      <DeleteConfirmation
        open={isDeleteDialogOpen}
        title="Delete RIP"
        onClose={() => dispatch(closeDeleteDialog())}
        content={{
          mainText: `
            Are you sure you want to delete RIP <strong>${ripToDelete?.ripNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => dispatch(hideSnackbar())}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
